/**
 * Appels `/api/patient/*` depuis l’espace professionnel (même JWT).
 * Voir docs/api/HELPEUR_USAGE_patient.md
 */

import { apiFetch } from "./client";

function extractPayload<T>(raw: unknown): T | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "object" && !Array.isArray(raw) && "data" in raw) {
    return (raw as { data?: T }).data;
  }
  return raw as T;
}

export async function searchPatients(
  query: Record<string, string | number | undefined>
): Promise<{ ok: true; data: unknown[] } | { ok: false; message: string }> {
  const q = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== "") q.set(k, String(v));
  });
  const out = await apiFetch<unknown>(`/patient/patients/search?${q.toString()}`);
  if (!out.ok) return { ok: false, message: out.message };
  const raw = extractPayload<unknown>(out.data) ?? out.data;
  const list = Array.isArray(raw) ? raw : [];
  return { ok: true, data: list };
}

export type CreatePatientBody = Record<string, unknown>;

export async function createPatient(
  body: CreatePatientBody
): Promise<{ ok: true; data: unknown } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>("/patient/patients", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

export async function getPatientByNip(
  nip: string
): Promise<{ ok: true; data: unknown } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>(`/patient/patients/${encodeURIComponent(nip)}`);
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

export async function getPatientHistory(
  nip: string
): Promise<{ ok: true; data: unknown } | { ok: false; message: string; errorCode?: string }> {
  const out = await apiFetch<unknown>(`/patient/patients/${encodeURIComponent(nip)}/history`);
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

export async function getPatientMedicalFilePro(
  nip: string
): Promise<
  { ok: true; data: unknown } | { ok: false; message: string; errorCode?: string; meta?: Record<string, unknown> }
> {
  const out = await apiFetch<unknown>(`/patient/dossiers-medicaux/${encodeURIComponent(nip)}`);
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode, meta: out.meta };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

/** Liste plate ou emballée (`consultations`, `items`, `history`, etc.). */
function extractConsultationsList(raw: unknown): unknown[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "object") return [];
  const tryArrays = (o: Record<string, unknown>): unknown[] | null => {
    for (const k of ["consultations", "items", "rows", "list", "results", "records", "history", "data"] as const) {
      const v = o[k];
      if (Array.isArray(v)) return v;
    }
    return null;
  };
  const top = tryArrays(raw as Record<string, unknown>);
  if (top) return top;
  const inner = (raw as Record<string, unknown>).data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    const nested = tryArrays(inner as Record<string, unknown>);
    if (nested) return nested;
  }
  return [];
}

export async function getPatientConsultationsPro(
  patientNip: string
): Promise<
  { ok: true; data: unknown[] } | { ok: false; message: string; errorCode?: string; meta?: Record<string, unknown> }
> {
  const out = await apiFetch<unknown>(
    `/patient/consultations/patient/${encodeURIComponent(patientNip)}`
  );
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode, meta: out.meta };
  const raw = extractPayload<unknown>(out.data) ?? out.data;
  const list = extractConsultationsList(raw);
  return { ok: true, data: list };
}

export async function getPatientPrescriptionsPro(
  patientNip: string
): Promise<
  { ok: true; data: unknown[] } | { ok: false; message: string; errorCode?: string; meta?: Record<string, unknown> }
> {
  const out = await apiFetch<unknown>(
    `/patient/ordonnances/patient/${encodeURIComponent(patientNip)}`
  );
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode, meta: out.meta };
  const raw = extractPayload<unknown>(out.data) ?? out.data;
  const list = Array.isArray(raw) ? raw : [];
  return { ok: true, data: list };
}

export async function getPatientVaccinationsPro(
  patientNip: string
): Promise<
  { ok: true; data: unknown[] } | { ok: false; message: string; errorCode?: string; meta?: Record<string, unknown> }
> {
  const out = await apiFetch<unknown>(
    `/patient/vaccinations/patient/${encodeURIComponent(patientNip)}`
  );
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode, meta: out.meta };
  const raw = extractPayload<unknown>(out.data) ?? out.data;
  const list = Array.isArray(raw) ? raw : [];
  return { ok: true, data: list };
}

export async function requestPatientAccessOtp(
  patientNip: string
): Promise<
  | { ok: true; data: { otpRequestId: string; userHint: string } }
  | { ok: false; message: string; status?: number; errorCode?: string }
> {
  const out = await apiFetch<unknown>(
    `/patient/patients/${encodeURIComponent(patientNip)}/access/request-otp`,
    { method: "POST", skipIdentityUnverifiedHandling: true }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  const payload = extractPayload<{ otpRequestId?: string; userHint?: string }>(out.data);
  return {
    ok: true,
    data: {
      otpRequestId: String(payload?.otpRequestId ?? ""),
      userHint: String(payload?.userHint ?? ""),
    },
  };
}

export async function verifyPatientAccessOtp(
  patientNip: string,
  otpRequestId: string,
  otpCode: string
): Promise<{ ok: true } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>(
    `/patient/patients/${encodeURIComponent(patientNip)}/access/verify-otp`,
    {
      method: "POST",
      body: JSON.stringify({ otpRequestId, otpCode }),
      skipIdentityUnverifiedHandling: true,
    }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true };
}

export async function createMedicalRecord(
  body: Record<string, unknown>
): Promise<{ ok: true; data: unknown } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>("/patient/dossiers-medicaux", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

/**
 * Met à jour les informations personnelles d'un dossier médical existant.
 * Body volontairement limité à `informationsPersonnelles` — pour les champs cliniques
 * (`informationsMedicales`), utiliser `updateDossierMedicalInfoPro` ci-dessous : même route PUT,
 * acceptée pour professionnel/établissement avec lien actif + identité vérifiée (et admin),
 * mais toujours refusée pour un compte patient.
 */
export async function updateDossierPersonalInfoPro(
  patientNip: string,
  informationsPersonnelles: Record<string, unknown>
): Promise<{ ok: true; data: unknown } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>(`/patient/dossiers-medicaux/${encodeURIComponent(patientNip)}`, {
    method: "PUT",
    body: JSON.stringify({ informationsPersonnelles }),
  });
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

/**
 * Met à jour les informations médicales d'un dossier existant (groupe sanguin, poids, taille,
 * électrophorèse, allergies, intolérances, antécédents, vaccinations). Aucun `motif` requis ici
 * (à la différence de la correction admin/vérificateur `PATCH .../informations-medicales`) — un
 * simple PUT, exactement comme pour `informationsPersonnelles`.
 */
export async function updateDossierMedicalInfoPro(
  patientNip: string,
  informationsMedicales: Record<string, unknown>
): Promise<{ ok: true; data: unknown } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>(`/patient/dossiers-medicaux/${encodeURIComponent(patientNip)}`, {
    method: "PUT",
    body: JSON.stringify({ informationsMedicales }),
  });
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

/** Génère (ou récupère l'URL déjà générée) le PDF récapitulatif d'un dossier médical. */
export async function getMedicalFilePdfPro(
  patientNip: string
): Promise<{ ok: true; pdfUrl: string } | { ok: false; message: string; errorCode?: string }> {
  const out = await apiFetch<unknown>(
    `/patient/dossiers-medicaux/${encodeURIComponent(patientNip)}/pdf`,
    { method: "POST" }
  );
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  const payload = extractPayload<{ pdfUrl?: string }>(out.data);
  const pdfUrl = payload?.pdfUrl;
  if (typeof pdfUrl !== "string" || !pdfUrl) return { ok: false, message: "URL PDF absente dans la réponse." };
  return { ok: true, pdfUrl };
}

/** Génère le certificat de vaccination (PDF) d'un patient. */
export async function getVaccinationCertificatePro(
  patientNip: string
): Promise<{ ok: true; pdfUrl: string } | { ok: false; message: string; errorCode?: string }> {
  const out = await apiFetch<unknown>(
    `/patient/vaccinations/patient/${encodeURIComponent(patientNip)}/certificate`,
    { method: "POST" }
  );
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  const payload = extractPayload<{ pdfUrl?: string }>(out.data);
  const pdfUrl = payload?.pdfUrl;
  if (typeof pdfUrl !== "string" || !pdfUrl) return { ok: false, message: "URL PDF absente dans la réponse." };
  return { ok: true, pdfUrl };
}

export async function createConsultationPro(
  body: Record<string, unknown>
): Promise<
  { ok: true; data: unknown } | { ok: false; message: string; status?: number; errorCode?: string }
> {
  const out = await apiFetch<unknown>("/patient/consultations", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

/** Met à jour une consultation encore ouverte (observations, complement) avant sa clôture. */
export async function updateConsultationPro(
  consultationId: string,
  body: Record<string, unknown>
): Promise<
  { ok: true; data: unknown } | { ok: false; message: string; status?: number; errorCode?: string }
> {
  const out = await apiFetch<unknown>(
    `/patient/consultations/${encodeURIComponent(consultationId)}`,
    {
      method: "PUT",
      body: JSON.stringify(body),
    }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

export async function closeConsultationPro(
  consultationId: string,
  body: Record<string, unknown>
): Promise<
  { ok: true; data: unknown } | { ok: false; message: string; status?: number; errorCode?: string }
> {
  const out = await apiFetch<unknown>(
    `/patient/consultations/${encodeURIComponent(consultationId)}/close`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

/** Génère (ou récupère l'URL déjà générée — snapshot figé au 1er appel) le PDF d'une consultation. */
export async function getConsultationPdfPro(
  consultationId: string
): Promise<{ ok: true; pdfUrl: string } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(
    `/patient/consultations/${encodeURIComponent(consultationId)}/pdf`,
    { method: "POST" }
  );
  if (!out.ok) return { ok: false, message: out.message };
  const payload = extractPayload<{ pdfUrl?: string }>(out.data);
  const pdfUrl = payload?.pdfUrl;
  if (typeof pdfUrl !== "string" || !pdfUrl) return { ok: false, message: "URL PDF absente dans la réponse." };
  return { ok: true, pdfUrl };
}

/** Génère (ou récupère l'URL déjà générée — snapshot figé au 1er appel) le PDF d'une ordonnance. */
export async function getOrdonnancePdfPro(
  ordonnanceId: string
): Promise<{ ok: true; pdfUrl: string } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(
    `/patient/ordonnances/${encodeURIComponent(ordonnanceId)}/pdf`,
    { method: "POST" }
  );
  if (!out.ok) return { ok: false, message: out.message };
  const payload = extractPayload<{ pdfUrl?: string }>(out.data);
  const pdfUrl = payload?.pdfUrl;
  if (typeof pdfUrl !== "string" || !pdfUrl) return { ok: false, message: "URL PDF absente dans la réponse." };
  return { ok: true, pdfUrl };
}

export async function createOrdonnancePro(
  body: Record<string, unknown>
): Promise<{ ok: true; data: unknown } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>("/patient/ordonnances", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

export async function createVaccinationPro(
  body: Record<string, unknown>
): Promise<{ ok: true; data: unknown } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>("/patient/vaccinations", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

export async function archivePatient(
  nip: string
): Promise<{ ok: true } | { ok: false; message: string; status?: number }> {
  const out = await apiFetch<unknown>(`/patient/patients/${encodeURIComponent(nip)}`, {
    method: "DELETE",
  });
  if (!out.ok) return { ok: false, message: out.message, status: out.status };
  return { ok: true };
}

export type RaisonArchivage = {
  _id: string;
  code: string;
  libelle: string;
  description: string | null;
  actif: boolean;
};

export type ArchiverPatientBody = {
  etablissementId: string;
  raisonArchivageId?: string;
  motifLibre?: string;
};

export async function getRaisonsArchivage(): Promise<
  { ok: true; data: RaisonArchivage[] } | { ok: false; message: string }
> {
  console.log("[getRaisonsArchivage] → GET /public/raisons-archivage");
  const out = await apiFetch<unknown>("/public/raisons-archivage", { skipAuth: true });
  console.log("[getRaisonsArchivage] ← réponse brute apiFetch:", out);
  if (!out.ok) {
    console.error("[getRaisonsArchivage] ✗ erreur API:", out.message);
    return { ok: false, message: out.message };
  }
  const raw = extractPayload<unknown>(out.data) ?? out.data;
  console.log("[getRaisonsArchivage] data extrait (après extractPayload):", raw);
  const list = Array.isArray(raw) ? (raw as RaisonArchivage[]) : [];
  console.log("[getRaisonsArchivage] ✓ liste finale:", list.length, "raison(s)", list);
  return { ok: true, data: list };
}

export async function archiverPatient(
  nip: string,
  body: ArchiverPatientBody
): Promise<{ ok: true; data: unknown } | { ok: false; message: string; status?: number }> {
  const out = await apiFetch<unknown>(`/patient/${encodeURIComponent(nip)}/archiver`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message, status: out.status };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}

export type TypeDocument =
  | "compte_rendu" | "ordonnance" | "analyse" | "imagerie" | "certificat"
  | "diplome" | "carte_professionnel" | "agrement" | "autorisation"
  | "rapport" | "statistique" | "communique" | "autre";

export const ALLOWED_MIME_TYPES = [
  "image/png", "image/jpeg", "image/jpg",
  "application/pdf", "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const;

export async function addDocumentToConsultation(
  consultationId: string,
  doc: {
    bucketUrl: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    type: TypeDocument;
    description?: string;
  }
): Promise<{ ok: true; data: unknown } | { ok: false; message: string; errorCode?: string }> {
  const out = await apiFetch<unknown>(
    `/patient/consultations/${encodeURIComponent(consultationId)}/documents`,
    { method: "POST", body: JSON.stringify(doc) }
  );
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: extractPayload(out.data) ?? out.data };
}
